import { ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import type { FC, ReactNode } from 'react';

import * as styles from './section-layout.css';

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface SectionStackProps {
  children: ReactNode;
  tight?: boolean;
  className?: string;
}

export const SectionStack: FC<SectionStackProps> = ({
  children,
  tight = false,
  className,
}) => (
  <div
    className={joinClasses(styles.stack, tight && styles.stackTight, className)}
  >
    {children}
  </div>
);

interface SectionControlRowProps {
  label: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  coachmarkTarget?: string;
}

export const SectionControlRow: FC<SectionControlRowProps> = ({
  label,
  description,
  children,
  coachmarkTarget,
}) => (
  <div className={styles.controlRow} data-coachmark-target={coachmarkTarget}>
    <div className={styles.controlInfo}>
      <span className={styles.controlLabel}>{label}</span>
      {description && (
        <span className={styles.controlDescription}>{description}</span>
      )}
    </div>
    <div className={styles.controlSlot}>{children}</div>
  </div>
);

interface SectionPanelProps {
  children: ReactNode;
  className?: string;
  'data-section'?: string;
}

export const SectionPanel: FC<SectionPanelProps> = ({
  children,
  className,
  'data-section': dataSection,
}) => (
  <div
    className={joinClasses(styles.panel, className)}
    data-section={dataSection}
  >
    {children}
  </div>
);

interface SectionPanelHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export const SectionPanelHeader: FC<SectionPanelHeaderProps> = ({
  title,
  description,
  action,
}) => (
  <div className={styles.panelHeader}>
    <div className={styles.panelCopy}>
      <span className={styles.panelTitle}>{title}</span>
      {description && (
        <span className={styles.panelDescription}>{description}</span>
      )}
    </div>
    {action}
  </div>
);

export const SectionPanelHint: FC<{ children: ReactNode }> = ({ children }) => (
  <span className={styles.panelHint}>{children}</span>
);

export const SectionEmpty: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.empty}>{children}</div>
);

interface SectionNoticeProps {
  children: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  variant?: 'info' | 'warning' | 'system';
}

export const SectionNotice: FC<SectionNoticeProps> = ({
  children,
  icon,
  action,
  variant = 'info',
}) => (
  <div className={styles.notice} data-variant={variant}>
    {icon && <span className={styles.noticeIcon}>{icon}</span>}
    <div className={styles.noticeContent}>{children}</div>
    {action}
  </div>
);

interface SectionTableRowProps {
  children: ReactNode;
  action?: ReactNode;
  indented?: boolean;
  spaced?: boolean;
}

export const SectionTableRow: FC<SectionTableRowProps> = ({
  children,
  action,
  indented = false,
  spaced = false,
}) => (
  <div
    className={styles.tableRow}
    data-indented={indented || undefined}
    data-spaced={spaced || undefined}
  >
    <div
      className={joinClasses(
        styles.tableMain,
        indented && styles.tableMainInline
      )}
    >
      {children}
    </div>
    {action && <div className={styles.tableAction}>{action}</div>}
  </div>
);

export const SectionRemoveButton: FC<{
  children?: ReactNode;
  onClick: () => void;
  title?: string;
}> = ({ children = '×', onClick, title }) => (
  <button className={styles.removeButton} onClick={onClick} title={title}>
    {children}
  </button>
);

export const SectionAddLink: FC<{
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}> = ({ children, onClick, disabled = false, 'data-testid': testId }) => (
  <button
    type="button"
    className={styles.addLink}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </button>
);

export const SectionDivider: FC = () => <div className={styles.divider} />;

/**
 * A setting's part for one target table. With several tables each part
 * stands under its table's name; a single table needs none.
 */
export const SectionTableGroup: FC<{ name?: string; children: ReactNode }> = ({
  name,
  children,
}) => (
  <div className={styles.tableGroup}>
    {name && (
      <span className={styles.tableGroupName} title={name}>
        {name}
      </span>
    )}
    {children}
  </div>
);

interface SectionMappingGridProps {
  /** Names the left side: where the values come from. */
  sourceLabel: ReactNode;
  /** Names the right side: what gets the values. */
  targetLabel: ReactNode;
  /** The side that belongs to the workflow; the other one is a table. */
  workflowSide: 'source' | 'target';
  children: ReactNode;
}

/**
 * A mapping as pairs: on the left what a value is taken from, on the right
 * what it fills. The head names both sides and marks the workflow's one, so
 * it is clear which fields are the table's and which the workflow's.
 */
export const SectionMappingGrid: FC<SectionMappingGridProps> = ({
  sourceLabel,
  targetLabel,
  workflowSide,
  children,
}) => (
  <div className={styles.mappingGrid} data-workflow-side={workflowSide}>
    <div className={styles.mappingGridHead}>
      <span
        className={styles.mappingSide}
        data-workflow={workflowSide === 'source' || undefined}
      >
        {sourceLabel}
      </span>
      <span aria-hidden="true" />
      <span
        className={styles.mappingSide}
        data-workflow={workflowSide === 'target' || undefined}
      >
        {targetLabel}
      </span>
    </div>
    {children}
  </div>
);

interface SectionMappingPairProps {
  /** Left: what the value is taken from (a choice or a fixed value). */
  source: ReactNode;
  /** Right: the field that gets the value. */
  target: ReactNode;
  /**
   * The sides that are given: a field's name and type, nothing to choose.
   * They are filled; a side that holds a control brings its own border.
   */
  fixed: 'source' | 'target' | 'both' | 'none';
  /** Between both: how they fit; an arrow while nothing is chosen. */
  link?: ReactNode;
  /** What belongs to the pair, below it at full width. */
  children?: ReactNode;
}

/** One pair of a mapping: source on the left, target on the right. */
export const SectionMappingPair: FC<SectionMappingPairProps> = ({
  source,
  target,
  fixed,
  link,
  children,
}) => (
  <div className={styles.mappingPair} data-streamlain-mapping-row>
    <div className={styles.mappingPairRow}>
      <div
        className={styles.mappingPairSide}
        data-side="source"
        data-fixed={fixed === 'source' || fixed === 'both' || undefined}
      >
        {source}
      </div>
      <span className={styles.mappingPairLink}>
        {link ?? <ArrowRightSmallIcon aria-hidden="true" />}
      </span>
      <div
        className={styles.mappingPairSide}
        data-side="target"
        data-fixed={fixed === 'target' || fixed === 'both' || undefined}
      >
        {target}
      </div>
    </div>
    {children}
  </div>
);

/** A labelled control of a setting: the label, the control, an optional hint. */
export const SectionField: FC<{
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}> = ({ label, hint, children }) => (
  <div className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    {children}
    {hint && <span className={styles.hint}>{hint}</span>}
  </div>
);

/** A quiet sentence that explains a setting's current state. */
export const SectionHint: FC<{
  children: ReactNode;
  tone?: 'neutral' | 'warning';
}> = ({ children, tone = 'neutral' }) => (
  <p className={styles.hint} data-tone={tone}>
    {children}
  </p>
);

/** Placeholder rows while a setting's content loads. */
export const SectionSkeleton: FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className={styles.mappingGrid} aria-busy="true">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className={styles.skeletonRow} />
    ))}
  </div>
);
